import type { PopoverOrigin } from '@mui/material';
import Menu from '@mui/material/Menu';
import { INTEGRATION_PROVIDERS, type PlanBillingData, type SyncStatus } from '@vy/types';
import { isNotEmptyString, isNotNullOrUndefined } from '@vy/utils';
import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useContractPage } from '../../../../hooks/requests/pages/use-contract-page';
import { prefetchContractProductsOverviews } from '../../../../hooks/requests/use-contract-products-overviews';
import { useActivatedIntegration } from '../../../../hooks/use-activated-integration';
import { prefetchNetSuiteUsers } from '../../../../hooks/requests/integrations';
import { useMenu } from '../../../../hooks/use-menu';
import { hasNetSuiteIntegration } from '../../../../utils/integration-utils';
import { findIntegrationByProviderInEntities } from '@vy/services';
import { ActivateContractMenuItem } from './contract-page-header-menu-components/ActivateContractMenuItem';
import { DeleteContractMenuItem } from './contract-page-header-menu-components/DeleteContractMenuItem';
import { MenuItemContractRenewal } from './contract-page-header-menu-components/MenuItemContractRenewal';
import { MenuItemNetSuiteExportContract } from './contract-page-header-menu-components/MenuItemNetSuiteExportContract';
import { MenuItemReviseContract } from './contract-page-header-menu-components/MenuItemReviseContract';
import { MenuItemTerminateContract } from './contract-page-header-menu-components/MenuItemTerminateContract';
import { MenuItemUpdateContract } from './contract-page-header-menu-components/MenuItemUpdateContract';
import {
  DEFAULT_MENU_ANCHOR_ORIGIN,
  DEFAULT_MENU_TRANSFORM_ORIGIN,
} from './contract-page-header-menu-consts/contract-page-default-menu';
import { MenuItemRenewOptions } from './contract-page-header-menu-components/MenuItemRenewOptions/MenuItemRenewOptions';
import { useDialog } from '../../../../hooks/use-dialog';
import { CloneContractMenuItem } from './contract-page-header-menu-components/CloneContractMenuItem';
import { CreateContractDialog } from '../../../plans/GetStartedPlansPage/components/CardsSections/components/CreateContractDialog';

type ContractPageHeaderMenuProps = {
  children?: React.ReactElement;
  isActive: boolean;
  isTerminated: boolean;
  contractId: string;
  planId: string;
  planName: string;
  billingData: PlanBillingData;
  customerName: string;
  customerId: string;
  contractStartDate: Date;
  syncStatus: SyncStatus;
  contractEndDate?: Date;
  contractName: string;
  menuAnchorOrigin?: PopoverOrigin;
  menuTransformOrigin?: PopoverOrigin;
  isContractEditable: boolean;
};

export const ContractPageHeaderMenu: React.FC<ContractPageHeaderMenuProps> = ({
  children,
  isActive,
  contractId,
  planId,
  planName,
  billingData,
  customerName,
  customerId,
  contractStartDate,
  contractEndDate,
  isTerminated,
  contractName,
  menuAnchorOrigin,
  menuTransformOrigin,
  isContractEditable,
}) => {
  const { isOpen, handleMenuOpen, handleMenuClose, anchorElement } = useMenu();
  const {
    isDialogOpen: isCreateContractDialogOpen,
    handleOpen: handleCreateContractDialogOpen,
    handleClose: handleCreateContractDialogClose,
  } = useDialog();

  const queryClient = useQueryClient();
  const [shouldPrefetch, setShouldPrefetch] = useState(false);

  const isNetSuiteConnected = useActivatedIntegration(INTEGRATION_PROVIDERS.netSuite);

  const { data: contractPageResult } = useContractPage(contractId, {
    enabled: shouldPrefetch,
  });

  const isContractWasRenewed = contractPageResult?.contractPageData.contract.wasRenewed ?? false;

  const customer = contractPageResult?.contractPageData.contract.customer;
  const customerNetSuiteEntity = isNotNullOrUndefined(customer?.integrationEntities)
    ? findIntegrationByProviderInEntities(customer.integrationEntities, INTEGRATION_PROVIDERS.netSuite)
    : null;
  const customerErpId = customerNetSuiteEntity?.integrationExternalId;

  const hasNetSuiteSyncStatus = hasNetSuiteIntegration(
    contractPageResult?.contractPageData.contract.integrationEntities,
  );
  const isContractHasPhases = (contractPageResult?.contractPageData?.versions.length ?? 0) > 1;

  useQuery({
    queryKey: [contractId, customerErpId],
    queryFn: async () => {
      await prefetchContractProductsOverviews(contractId, queryClient);

      if (isNotEmptyString(customerErpId)) {
        await prefetchNetSuiteUsers(customerErpId, queryClient);
        setShouldPrefetch(false);
      }
    },
    enabled: shouldPrefetch,
  });

  const handleMouseOver = () => {
    if (isNetSuiteConnected) {
      setShouldPrefetch(true);
    }
  };

  return (
    <>
      {isNotNullOrUndefined(children) &&
        React.cloneElement(children, {
          onClick: handleMenuOpen,
          onMouseOver: handleMouseOver,
        })}

      <Menu
        anchorOrigin={menuAnchorOrigin ?? DEFAULT_MENU_ANCHOR_ORIGIN}
        transformOrigin={menuTransformOrigin ?? DEFAULT_MENU_TRANSFORM_ORIGIN}
        open={isOpen}
        anchorEl={anchorElement}
        onClose={handleMenuClose}
        slotProps={{
          paper: {
            elevation: 3,
            sx: {
              width: 248,
              borderRadius: 2,
              overflow: 'hidden',
            },
          },
        }}
      >
        {isTerminated && contractEndDate && (
          <MenuItemContractRenewal
            isTerminated={isTerminated}
            contractId={contractId}
            contractEndDate={contractEndDate}
          />
        )}

        <MenuItemUpdateContract
          contractId={contractId}
          isEditable={isContractEditable}
          contractStartDate={contractStartDate}
          billingInterval={billingData.billingInterval}
          disabled={isTerminated || isContractHasPhases || isContractWasRenewed}
        />

        <MenuItemReviseContract
          contractId={contractId}
          contractStartDate={contractStartDate}
          contractEndDate={contractEndDate}
          disabled={isTerminated || isContractEditable}
        />

        <CloneContractMenuItem onClick={handleCreateContractDialogOpen} />

        <MenuItemRenewOptions contractId={contractId} isAutoRenewal={billingData.autoRenewal} />

        {isNetSuiteConnected && (
          <MenuItemNetSuiteExportContract
            contractId={contractId}
            customerId={customerId}
            planId={planId}
            disabled={isTerminated}
            isSynced={hasNetSuiteSyncStatus}
            customerErpId={customerErpId}
          />
        )}

        <ActivateContractMenuItem disabled={isTerminated} contractId={contractId} isActive={isActive} />

        <MenuItemTerminateContract
          contractId={contractId}
          customerName={customerName}
          isActive={isActive}
          contractStartDate={contractStartDate}
          contractEndDate={contractEndDate}
        />

        <DeleteContractMenuItem
          contractId={contractId}
          contractName={contractName}
          disabled={!isContractEditable}
        />
      </Menu>
      <CreateContractDialog
        isOpen={isCreateContractDialogOpen}
        onClose={handleCreateContractDialogClose}
        cloneFromPlanId={planId}
        initialCustomerId={customerId}
        label="Clone Contract and Assign to a Customer"
      />
    </>
  );
};
